import {
  Megaphone,
  PenLine,
  CalendarClock,
  BarChart3,
  Radar,
} from "lucide-react";
import {
  HOME_FEATURE_CAMPAIGNS_TITLE,
  HOME_FEATURE_CAMPAIGNS_DESCRIPTION,
  HOME_FEATURE_CAMPAIGNS_CTA,
  HOME_FEATURE_REPORTS_TITLE,
  HOME_FEATURE_REPORTS_DESCRIPTION,
  HOME_FEATURE_REPORTS_CTA,
  HOME_FEATURE_CONTENT_TITLE,
  HOME_FEATURE_CONTENT_DESCRIPTION,
  HOME_FEATURE_CONTENT_CTA,
  HOME_FEATURE_SCHEDULE_TITLE,
  HOME_FEATURE_SCHEDULE_DESCRIPTION,
  HOME_FEATURE_SCHEDULE_CTA,
  HOME_FEATURE_COMPETITORS_TITLE,
  HOME_FEATURE_COMPETITORS_DESCRIPTION,
  HOME_FEATURE_COMPETITORS_CTA,
} from "../i18n/keysTranslations";

/**
 * @typedef {"campaigns" | "content" | "schedule" | "reports" | "competitors"} FeatureId
 * @typedef {"createCampaign" | "createWithIA" | "shareModal" | "competitorScope"} HomeFeatureRunId
 *
 * @typedef {{ type: "navigate", to: string }} HomeFeatureNavigateAction
 * @typedef {{ type: "run", id: HomeFeatureRunId }} HomeFeatureRunAction
 * @typedef {HomeFeatureNavigateAction | HomeFeatureRunAction} HomeFeatureAction
 *
 * @typedef {Object} HomeFeatureCatalogItem
 * @property {FeatureId} id
 * @property {string} titleKey
 * @property {string} descriptionKey
 * @property {import("lucide-react").LucideIcon} icon
 * @property {HomeFeatureAction} action
 * @property {string} ctaKey
 *
 * @typedef {{ type: "navigate", to: string }} ResolvedNavigateAction
 * @typedef {{ type: "run", onClick: Function }} ResolvedRunAction
 * @typedef {{ type: "none" }} ResolvedNoneAction
 * @typedef {ResolvedNavigateAction | ResolvedRunAction | ResolvedNoneAction} ResolvedFeatureAction
 *
 * @typedef {Partial<Record<HomeFeatureRunId, Function | null | undefined>>} HomeFeatureRunners
 */

/** @type {Record<string, FeatureId>} */
export const HOME_FEATURE_ID = {
  CAMPAIGNS: "campaigns",
  REPORTS: "reports",
  CONTENT: "content",
  SCHEDULE: "schedule",
  COMPETITORS: "competitors",
};

/** @type {FeatureId[]} */
export const HOME_FEATURE_IDS = [
  HOME_FEATURE_ID.CAMPAIGNS,
  HOME_FEATURE_ID.REPORTS,
  HOME_FEATURE_ID.CONTENT,
  HOME_FEATURE_ID.SCHEDULE,
  HOME_FEATURE_ID.COMPETITORS,
];

/** @type {HomeFeatureCatalogItem[]} */
export const HOME_FEATURES = [
  {
    id: HOME_FEATURE_ID.CAMPAIGNS,
    titleKey: HOME_FEATURE_CAMPAIGNS_TITLE,
    descriptionKey: HOME_FEATURE_CAMPAIGNS_DESCRIPTION,
    icon: Megaphone,
    action: { type: "run", id: "createCampaign" },
    ctaKey: HOME_FEATURE_CAMPAIGNS_CTA,
  },
  {
    id: HOME_FEATURE_ID.REPORTS,
    titleKey: HOME_FEATURE_REPORTS_TITLE,
    descriptionKey: HOME_FEATURE_REPORTS_DESCRIPTION,
    icon: BarChart3,
    action: { type: "navigate", to: "/report" },
    ctaKey: HOME_FEATURE_REPORTS_CTA,
  },
  {
    id: HOME_FEATURE_ID.CONTENT,
    titleKey: HOME_FEATURE_CONTENT_TITLE,
    descriptionKey: HOME_FEATURE_CONTENT_DESCRIPTION,
    icon: PenLine,
    action: { type: "run", id: "createWithIA" },
    ctaKey: HOME_FEATURE_CONTENT_CTA,
  },
  {
    id: HOME_FEATURE_ID.SCHEDULE,
    titleKey: HOME_FEATURE_SCHEDULE_TITLE,
    descriptionKey: HOME_FEATURE_SCHEDULE_DESCRIPTION,
    icon: CalendarClock,
    action: { type: "run", id: "shareModal" },
    ctaKey: HOME_FEATURE_SCHEDULE_CTA,
  },
  {
    id: HOME_FEATURE_ID.COMPETITORS,
    titleKey: HOME_FEATURE_COMPETITORS_TITLE,
    descriptionKey: HOME_FEATURE_COMPETITORS_DESCRIPTION,
    icon: Radar,
    action: { type: "run", id: "competitorScope" },
    ctaKey: HOME_FEATURE_COMPETITORS_CTA,
  },
];

/**
 * @param {HomeFeatureCatalogItem} feature
 * @param {HomeFeatureRunners} runners
 * @returns {ResolvedFeatureAction}
 */
export const resolveFeatureAction = (feature, runners) => {
  const { action } = feature;

  switch (action.type) {
    case "navigate":
      return { type: "navigate", to: action.to };
    case "run": {
      const onClick = runners[action.id];
      if (!onClick) {
        return { type: "none" };
      }
      return { type: "run", onClick };
    }
    default: {
      const _exhaustiveCheck = action;
      void _exhaustiveCheck;
      return { type: "none" };
    }
  }
};
